import { useCallback, useEffect, useRef, useState } from 'react';
import { api, buildFormData, buildQueryString, buildService } from '@rd-web-markets/shared/dist/services/service';
import { useServiceCall } from '@rd-web-markets/shared/dist/hooks/useServiceCall';
import { useTranslation } from 'react-i18next';

/**
 * All methods in this file execute code equivalent to
 * try {
 *   do_something()
 *   execute_on_success()
 * } catch (e) {
 *   execute_on_error()
 * } finally {
 *   execute_on_either_success_or_error()
 * }
 */

const useHttpCallback = _ref => {
  let {
    callback,
    setLoading = null,
    setResult = null,
    onSuccess = null,
    onError = null,
    onFinally = null,
    overrideHttpStatusErrorHandlers = null,
    modalConfirm = null
  } = _ref;
  const httpErrorHandledCallback = useServiceCall(useCallback(async function () {
    setLoading && setLoading(true);
    const result = await callback(...arguments);
    setResult && setResult(result);

    // onSuccess is technically not requried, as the success code can simply be part of the callback, but is added
    // to make the api clearer.
    onSuccess && onSuccess();

    // returns the promise with data just in case we need it somewhere
    return result;
  }, [callback, onSuccess, setLoading, setResult]), useCallback(() => {
    setLoading && setLoading(false);
    onFinally && onFinally();
  }, [onFinally, setLoading]), useCallback(() => {
    onError && onError();
  }, [onError]), overrideHttpStatusErrorHandlers, modalConfirm);
  return httpErrorHandledCallback;
};
const useHttpInterval = _ref2 => {
  let {
    callback,
    timeoutMilliseconds
  } = _ref2;
  return api.createInterval(callback, timeoutMilliseconds);
};

/**
 * Starts a polling interval that repeatedly calls `callback` every `intervalMs` milliseconds.
 * The interval is active only when `enabled` is true, and is automatically cleared when
 * `enabled` becomes false or the component unmounts.
 *
 * Intended for use cases like polling for status updates on pending background jobs.
 *
 * @param {Object} param0
 * @param {Function} param0.callback - The function to call on each interval tick
 * @param {boolean} [param0.enabled=true] - Whether polling is currently active
 * @param {number} [param0.intervalMs=5000] - Polling interval in milliseconds
 */
const usePolling = _ref3 => {
  let {
    callback,
    enabled = true,
    intervalMs = 5000
  } = _ref3;
  const intervalRef = useRef(null);
  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    intervalRef.current = setInterval(() => {
      callback();
    }, intervalMs);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [callback, enabled, intervalMs]);
};

/**
 * Used for GET /collection/:id or GET /collection requests
 * Used to fetch a single or multiple objects.
 * You can pass initialQueryParams - which will be used the first time the callback method is called from the useEffect.
 * You can then pass a queryParams JSON object each time you call the returned fetchItems method.
 * initialStateValue - is the initial state value (undefined by default). It could be [] when fetching collections or {} when fetching objects.
 * 
 * paginationResourceField - is used when the backend controller returns a paged response like so: { claim_groups: [...], pages: 14 }. In this calse it should be set to 'claim_groups'.
 * 
 * Runs the callback in a useEffect once the current react component is rendered.
 * @param {Object}  
 * @returns [item[s], setItem[s], fetchItem[s]]
 */
const useFetch = _ref4 => {
  let {
    callback,
    setLoading,
    initialQueryParams,
    initialStateValue = [],
    paginationResourceField = null,
    onSuccess,
    onError,
    onFinally,
    fetchImmediately = true,
    overrideHttpStatusErrorHandlers = null,
    modalConfirm = null
  } = _ref4;
  // items can also be a single item - depending on the endpoint we hit
  const [items, setItems] = useState(initialStateValue);
  const [pages, setPages] = useState(null);
  const fetchItems = useServiceCall(useCallback(async queryParams => {
    setLoading && setLoading(true);
    const allItems = await callback(queryParams);

    // This OR condition is required in case the frontend set the paginationResourceField, but did not send e.g. "page" query parameter
    // so the backend did not return a paged response.
    if (paginationResourceField && allItems[paginationResourceField]) {
      setItems(allItems[paginationResourceField]);
      setPages(allItems.pages);
    } else {
      setItems(allItems);
    }

    // onSuccess is technically not requried, as the success code can simply be part of the callback, but is added
    // to make the api clearer.
    onSuccess && onSuccess(allItems);

    // returns the promise with data just in case we need it somewhere
    return allItems;
  }, [callback, onSuccess, paginationResourceField, setLoading]), useCallback(() => {
    setLoading && setLoading(false);
    onFinally && onFinally();
  }, [onFinally, setLoading]), useCallback(() => {
    onError && onError();
  }, [onError]), overrideHttpStatusErrorHandlers, modalConfirm);
  useEffect(() => {
    if (fetchImmediately) {
      fetchItems(initialQueryParams || {});
    }
  }, [fetchImmediately, fetchItems, initialQueryParams]);
  return [items, setItems, fetchItems, pages];
};

/**
 * Used for GET /collection/:id or GET /collection requests
 * Used to fetch a single or multiple objects.
 * Check useFetch.
 * Note that for this method the onSuccess, onError and onFinally will be called on each fetch request.
 * 
 * Creates an interval once the current react component is rendered. The interval's repeat time is intervalTimeMs or 5000ms by default.
 * The interval can then be started and cleared at will.
 * @param {*} param0 
 * @returns { stateVar, setStateVar, fetchMethod, interval, setInterval }
 */
const useFetchWithInterval = _ref5 => {
  let {
    api,
    callback,
    setLoading,
    initialQueryParams,
    initialStateValue,
    intervalTimeMs,
    onSuccess,
    onError,
    onFinally,
    modalConfirm = null
  } = _ref5;
  const [interval, setInterval] = useState(null);
  const [stateVar, setStateVar, fetchMethod] = useFetch({
    callback,
    setLoading,
    initialQueryParams,
    initialStateValue,
    onSuccess,
    onError,
    onFinally,
    modalConfirm
  });

  // On the first render of the component where this method is used, we create the inverval.
  // At this point the interval is not started and no request is made. We need to call
  // interval.start() - to start it and interval.clear() - to clear it.
  useEffect(() => {
    const newInterval = api.base.createInterval(() => fetchMethod(), intervalTimeMs || 5000);
    setInterval(newInterval);
  }, [api.base, fetchMethod, intervalTimeMs]);
  return {
    stateVar,
    setStateVar,
    fetchMethod,
    interval,
    setInterval
  };
};
const useCreate = _ref6 => {
  let {
    callback,
    setLoading,
    onSuccess,
    onError,
    onFinally,
    modalConfirm = null
  } = _ref6;
  const createModel = useServiceCall(useCallback(async function () {
    setLoading && setLoading(true);
    const result = await callback(...arguments);

    // onSuccess is technically not requried, as the success code can simply be part of the callback, but is added
    // to make the api clearer.
    onSuccess && (await onSuccess(result));
    setLoading && setLoading(false);
    return result;
  }, [setLoading, callback, onSuccess]), useCallback(async () => {
    onFinally && (await onFinally());
    setLoading && setLoading(false);
  }, [onFinally, setLoading]), useCallback(async () => {
    onError && (await onError());
  }, [onError]), null, modalConfirm);
  return createModel;
};

/**
 * The resulting method accepts an object with the formDataJson field and any other fields.
 * It turns the formDataJson into a real form data - formData.
 * 
 * { field1: 1, value1, ..., formDataJson } => callback({ field1: value 1, ..., formData })
 * 
 * @param {*} param0 
 * @returns 
 */
const usePostFormData = _ref7 => {
  let {
    callback,
    setLoading,
    onSuccess,
    onError,
    onFinally,
    modalConfirm = null
  } = _ref7;
  const postFormData = useServiceCall(useCallback(async paramsObject => {
    setLoading && setLoading(true);
    const {
      formDataJson
    } = paramsObject;
    const formData = buildFormData(formDataJson);
    const result = await callback({
      ...paramsObject,
      formData
    });
    setLoading && setLoading(false);

    // onSuccess is technically not requried, as the success code can simply be part of the callback, but is added
    // to make the api clearer.
    onSuccess && onSuccess();
    return result;
  }, [setLoading, callback, onSuccess]), useCallback(() => {
    setLoading && setLoading(false);
    onFinally && onFinally();
  }, [onFinally, setLoading]), useCallback(() => {
    onError && onError();
  }, [onError]), null, modalConfirm);
  return postFormData;
};
const useUpdate = _ref8 => {
  let {
    callback,
    setLoading,
    onSuccess,
    onError,
    onFinally,
    modalConfirm = null
  } = _ref8;
  const updateModel = useServiceCall(useCallback(async paramsObject => {
    setLoading && setLoading(true);
    const result = await callback(paramsObject);
    setLoading && setLoading(false);

    // onSuccess is technically not requried, as the success code can simply be part of the callback, but is added
    // to make the api clearer.
    onSuccess && onSuccess();
    return result;
  }, [setLoading, callback, onSuccess]), useCallback(() => {
    setLoading && setLoading(false);
    onFinally && onFinally();
  }, [onFinally, setLoading]), useCallback(() => {
    onError && onError();
  }, [onError]), null, modalConfirm);
  return updateModel;
};
const useDelete = _ref9 => {
  let {
    callback,
    setLoading,
    onSuccess,
    onError,
    onFinally,
    modalConfirm = null
  } = _ref9;
  const deleteModel = useServiceCall(useCallback(async function () {
    setLoading && setLoading(true);
    const result = await callback(...arguments);
    setLoading && setLoading(false);

    // onSuccess is technically not requried, as the success code can simply be part of the callback, but is added
    // to make the api clearer.
    onSuccess && onSuccess();
    return result;
  }, [setLoading, callback, onSuccess]), useCallback(() => {
    setLoading && setLoading(false);
    onFinally && onFinally();
  }, [onFinally, setLoading]), useCallback(() => {
    onError && onError();
  }, [onError]), null, modalConfirm);
  return deleteModel;
};
const usePostFormDataFileFromEvent = _ref0 => {
  let {
    callback,
    setLoading,
    handleToaster,
    onSuccess,
    onError,
    onFinally,
    modalConfirm = null
  } = _ref0;
  const {
    t
  } = useTranslation();
  const defaultOnError = useCallback(() => handleToaster && handleToaster('error', t('upload_failed')), [handleToaster, t]);
  const uploadFile = usePostFormData({
    setLoading,
    onError: onError || defaultOnError,
    onSuccess,
    onFinally,
    callback,
    modalConfirm
  });

  // accepts the actual upload event, or a json of the form
  // {
  //    event: event,
  //    additionalData: { some additional key value pairs to add to the form data }
  // }
  const onUploadFile = eventOrObject => {
    let event, additionalData;

    // assume having the target property means its an event
    if (eventOrObject.target) {
      event = eventOrObject;
      additionalData = {};
    } else {
      event = eventOrObject.event;
      additionalData = eventOrObject.additionalData;
    }
    return uploadFile({
      formDataJson: {
        file: event.target.files[0],
        ...additionalData
      }
    });
  };
  return onUploadFile;
};
const serviceMethods = {
  useHttpCallback,
  useHttpInterval,
  usePolling,
  useFetch,
  useFetchWithInterval,
  useUpdate,
  useDelete,
  useCreate,
  usePostFormData,
  usePostFormDataFileFromEvent
};
export default serviceMethods;